import { useApi } from '@paradigm/api';

import {
  Drfq,
  DrfqState,
  DrfqVenueCode,
} from '#/unified-rfqs/entities/drfq/domain';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import {
  CreateRfqReq,
  CreateRfqReqLeg,
} from '#/unified-rfqs/repositories/drfq-types';
import * as Legs from '#/unified-rfqs/utils/legs';

import { useRfqBuilderView, View } from './view';
import { FutureLegView, LegView, OptionLegView } from './leg-view';
import { extractMessage } from './parse-resp-error';

interface Success {
  readonly ok: true;
  readonly data: Drfq;
}

interface Failure {
  readonly ok: false;
  readonly message: string;
}

export interface DrfqSendCallback {
  (resp: Success | Failure): void;
}

export function useSendBuilderRfq() {
  const view = useRfqBuilderView();
  const { isLoading, send } = useSendRfqBase();

  return {
    isLoading,
    send: (cb: DrfqSendCallback, options?: { state: DrfqState }) => {
      if (!view.isValid) throw new TypeError(INVALID_ERROR_MESSAGE);
      const createRfqReq = prepareCreateRfqReq(view, options?.state);
      send(createRfqReq, cb);
    },
  };
}

export function useSendRfq(payload: CreateRfqReq) {
  const { isLoading, send } = useSendRfqBase();

  return {
    isLoading,
    send: (cb: DrfqSendCallback) => {
      send(payload, cb);
    },
  };
}

function useSendRfqBase() {
  const { createRfq } = useDrfqService();
  const [{ isLoading }, send] = useApi(createRfq);

  return {
    isLoading,
    send: (payload: CreateRfqReq, cb: DrfqSendCallback) => {
      send(payload, (resp) => {
        if (resp.ok) {
          cb(resp);
        } else {
          const message = extractMessage(resp.data.data) ?? resp.message;
          cb({ ...resp, message });
        }
      });
    },
  };
}

const INVALID_ERROR_MESSAGE =
  'RFQ builder state is not valid. Make sure to' +
  ' only call this function when the state is valid.';

function prepareCreateRfqReq(
  view: View,
  state: DrfqState = 'OPEN',
): CreateRfqReq {
  const account_name = view.account.selectedOption ?? '';
  const venue = view.venue.selectedOption.value;

  const baseLegIndex = Legs.findBaseLegIndex(
    view.legs.map((leg) => ({
      quantity: leg.quantity.value,
      price: 'price' in leg ? leg.price.value : null,
      isHedge: leg.isHedge,
    })),
  );
  const baseLeg = view.legs[baseLegIndex];
  if (baseLeg == null) throw new Error(INVALID_ERROR_MESSAGE);

  const { kind, quantity } = baseLeg;
  if (kind == null || quantity.value == null)
    throw new Error(INVALID_ERROR_MESSAGE);

  return {
    venue,
    quantity: quantity.value,
    account_name,
    legs: view.legs.map((leg) => prepareLeg(leg, venue)),
    counterparties: view.counterparties.map((cp) => cp.desk_name),
    is_taker_anonymous: view.anonymous,
    state,
  };
}

function prepareLeg(legView: LegView, venue: string): CreateRfqReqLeg {
  switch (legView.kind) {
    case 'FUTURE':
      return prepareFutureLeg(legView, venue);
    case 'OPTION':
      return prepareUnpricedLeg(legView, venue);
    default:
      throw new TypeError('Leg should be either FUTURE or OPTION');
  }
}

function prepareFutureLeg(
  legView: FutureLegView,
  venue: string,
): CreateRfqReqLeg {
  const unpricedLeg = prepareUnpricedLeg(legView, venue);
  if (!legView.price.isSupported) return unpricedLeg;

  const hedgeLeg = { ...unpricedLeg, price: legView.price.value };
  return hedgeLeg;
}

function prepareUnpricedLeg(
  legView: FutureLegView | OptionLegView,
  venue: string,
): CreateRfqReqLeg {
  return {
    venue: venue as DrfqVenueCode,
    instrument_id: legView.instrument_id!,
    ratio: legView.ratio.value,
    side: legView.action.selectedOption,
  };
}
